import { useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Icon } from '../../icons';
import { registerModal } from '../../utils/backButtonManager';

export default function Modal({ title, children, onClose, wide = false, xl = false, xxl = false, popup = false, centered = false, bodyClassName = "", footer = null }) {
  const isPopup = popup || centered;
  const maxW = isPopup ? 400 : xxl ? 1000 : xl ? 800 : wide ? 640 : 520;

  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  useEffect(() => {
    const unregister = registerModal(() => {
      onCloseRef.current?.();
    });
    return unregister;
  }, []);

  const backdropRef = useRef(false);
  const handleBackdropMouseDown = useCallback((e) => {
    backdropRef.current = e.target === e.currentTarget;
  }, []);
  const handleBackdropTouchStart = useCallback((e) => {
    backdropRef.current = e.target === e.currentTarget;
  }, []);
  const handleBackdropClick = useCallback((e) => {
    if (backdropRef.current && e.target === e.currentTarget) {
      onCloseRef.current?.();
    }
    backdropRef.current = false;
  }, []);

  const content = (
    <div
      className={`fixed inset-0 flex justify-center ${
        isPopup ? 'items-center p-4' : 'items-end md:items-center p-0 md:p-4'
      }`}
      style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)', zIndex: 99999 }}
      onMouseDown={handleBackdropMouseDown}
      onTouchStart={handleBackdropTouchStart}
      onClick={handleBackdropClick}
    >
      <div
        className={`bg-white dark:bg-slate-900 w-full flex flex-col shadow-2xl relative ${
          isPopup
            ? 'rounded-2xl h-auto max-h-[90vh]'
            : 'rounded-t-2xl md:rounded-xl max-h-[92dvh] h-[92dvh] md:h-auto md:max-h-[90vh]'
        }`}
        style={{
          maxWidth: `min(100%, ${maxW}px)`,
          zIndex: 100000,
          paddingBottom: !footer ? 'max(1rem, env(safe-area-inset-bottom, 16px))' : undefined,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mobile drag handle indicator */}
        {!isPopup && (
          <div className="flex justify-center pt-2.5 pb-0.5 md:hidden flex-shrink-0 bg-white dark:bg-slate-900 rounded-t-2xl">
            <div className="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-700" />
          </div>
        )}

        {/* Header */}
        <div
          className={`flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex-shrink-0 sticky top-0 bg-white dark:bg-slate-900 ${
            isPopup ? 'rounded-t-2xl' : 'rounded-t-none md:rounded-t-xl'
          }`}
          style={{ zIndex: 100001 }}
        >
          <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100 my-0">{title}</h2>
          <button
            type="button"
            onClick={() => onCloseRef.current?.()}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-700 dark:hover:text-slate-100 transition-colors bg-transparent border-0 cursor-pointer"
          >
            <Icon name="x" size={14} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className={`p-4 overflow-y-auto overscroll-contain overflow-x-hidden ${isPopup ? '' : 'flex-1'} ${footer ? 'pb-4' : 'pb-12 md:pb-8'} ${bodyClassName}`}>
          {children}
        </div>

        {/* Pinned Footer */}
        {footer && (
          <div
            className={`px-4 py-3 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex-shrink-0 ${
              isPopup ? 'rounded-b-2xl' : 'rounded-b-none md:rounded-b-xl'
            }`}
            style={{
              paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 16px))',
              zIndex: 100001,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  const target = (typeof document !== 'undefined' && (document.querySelector('ion-app') || document.getElementById('root') || document.body)) || null;
  if (!target) return null;
  return ReactDOM.createPortal(content, target);
}
